import { ScopedLogger } from "./log.js";

const AgentLegActive = "Active";
const AgentLegDialing = "Dialing";
const AgentLegDisconnected = "Disconnected";
const logger = new ScopedLogger("AgentLegManager");

export default class AgentLegManager {
  constructor(audioRemoteElementRef) {
    this.phone = null;
    this.agentLegId = null;
    this.hasActiveCall = false;
    this.lastAgentLegState = null;
    this.registeredCallback = null;
    this.currentActiveCall = null;
    this.callConnectedCallback = null;
    this.audioRemoteElementRef = audioRemoteElementRef;
    this.localStream = null;
    this.currentRemoteStream = null;
    this.hasInitialized = false;
    this.phoneConnectionConfig = {
      reconnectIntervalMin: 2,
      reconnectIntervalMax: 30,
      registerExpires: 600,
      useSessionTimer: false,
      keepAlivePing: 10,
      pingTimeout: false,
      restoreCall: true,
      restoreCallMaxDelay: 20,
    };
  }

  get isConnected() {
    return this.hasActiveCall;
  }

  get isInitialized() {
    return this.hasInitialized;
  }

  initialize(agentSettings, agentId, registeredCallback, callConnectedCallback) {
    return new Promise((resolve, reject) => {
      if (!agentSettings) {
        throw new Error("Cannot initialize agent leg with blank settings");
      }

      logger.info("Registering softphone");

      this.hasInitialized = true;
      this.registeredCallback = registeredCallback;
      this.callConnectedCallback = callConnectedCallback;

      if (!this.phone) {
        this.phone = new AudioCodesUA();
        this.phone.setUserAgent("LLS Service Monitor");

        const webRTCWSSUrls = this.#retrieveWebRTCUrls(agentSettings);

        this.softphoneDialNumber = agentSettings.webRTCDNIS;
        this.phone.setServerConfig(
          webRTCWSSUrls,
          agentSettings.webRTCServerDomain,
          agentSettings.webRTCICEUrls,
        );
        this.phone.setAccount(agentId, agentId, "", "");

        this.#setupPhoneConfiguration();
        this.phone.setListeners(this.#retrievePhoneListeners(resolve, reject));

        this.phone.init(true);
      }
    });
  }

  processAgentLegEvent(event) {
    if (this.lastAgentLegState === event.status) {
      logger.debug("Ignoring agent leg state as it did not change");
      return;
    } else {
      logger.log(`Agent leg status changed from ${this.lastAgentLegState} to ${event.status}`);
    }

    this.agentLegId = event.agentLegId;

    switch (event.status) {
      case AgentLegDialing: {
        logger.info("Agent leg is attempting to establish");
        this.#callAgentLeg();
        break;
      }
      case AgentLegActive: {
        if (!this.isConnected) {
          this.#restoreSoftphoneCall();
        }
        break;
      }
    }

    this.lastAgentLegState = event.status;
  }

  shutdown() {
    // Save any restore state if applicable
    if (this.currentActiveCall) {
      this.#saveRestoreData();
    }
  }

  // INTERNAL METHODS

  #callAgentLeg() {
    if (!this.agentLegId) {
      logger.error("Failed to call agent leg as there's no agent leg ID");
      return;
    }

    const params = [`X-InContact-AgentLegId: ${this.agentLegId}`];
    logger.info("Dialing agent leg number");
    logger.debug("Calling phone.call() with agentLegId header", {
      dnis: this.softphoneDialNumber,
      params,
    });
    this.currentActiveCall = this.phone.call(false, this.softphoneDialNumber, params);
  }

  #retrieveBrowserConstraints() {
    return {
      chrome: { audio: { echoCancellation: true, noiseSuppression: true } },
      brave: { audio: { echoCancellation: true, noiseSuppression: true } },
      firefox: { audio: { echoCancellation: true, noiseSuppression: true } },
      safari: { audio: { echoCancellation: true, noiseSuppression: true } },
      other: { audio: { echoCancellation: true, noiseSuppression: true } },
    };
  }

  #setupPhoneConfiguration() {
    this.phone.setReconnectIntervals(
      this.phoneConnectionConfig.reconnectIntervalMin,
      this.phoneConnectionConfig.reconnectIntervalMax,
    );
    this.phone.setRegisterExpires(this.phoneConnectionConfig.registerExpires);
    this.phone.setUseSessionTimer(this.phoneConnectionConfig.useSessionTimer);
    this.phone.setBrowsersConstraints(this.#retrieveBrowserConstraints());
    this.phone.setWebSocketKeepAlive(
      this.phoneConnectionConfig.keepAlivePing,
      this.phoneConnectionConfig.pingTimeout,
    );
  }

  #retrieveWebRTCUrls(settings) {
    let webRTCWSSUrls;
    const restoredData = localStorage.getItem("sessionConnectedServer");
    if (restoredData !== null) {
      webRTCWSSUrls = this.#retrievePrimaryIceServer(settings.webRTCWSSUrls, restoredData);
      localStorage.removeItem("sessionConnectedServer");
    } else {
      webRTCWSSUrls = this.#retrievePrimaryIceServer(settings.webRTCWSSUrls, "");
    }
    return webRTCWSSUrls;
  }

  #retrievePhoneListeners(resolve, reject) {
    let hasRespondedInitially = false;

    // Set a 20 second timeout for registration
    let registrationTimeout = setTimeout(() => {
      if (!hasRespondedInitially) {
        reject();
      }
    }, 20 * 1000);

    return {
      loginStateChanged: (isLogin, cause) => {
        logger.debug("phone.loginStateChanged isLogin: %o cause: %o", isLogin, cause);
        logger.info(isLogin ? "Registered softphone" : "Softphone unregistered");

        if (!hasRespondedInitially && isLogin) {
          hasRespondedInitially = true;
          clearTimeout(registrationTimeout);
          resolve();
        }

        if (isLogin && this.registeredCallback) {
          this.registeredCallback(cause);
        }
      },
      outgoingCallProgress: (call, response) => {
        logger.debug("phone.outgoingCallProgress: Call: %o Response: %o", call, response);
      },
      callTerminated: (call, message, cause, redirectTo) => {
        this.hasActiveCall = false;
        logger.debug(
          "phone.callTerminated, call: %o message: %o cause: %o redirectTo: %o",
          call,
          message,
          cause,
          redirectTo,
        );
      },
      callConfirmed: (call, message, cause) => {
        const sbcFqdnHeader = message.getHeader("X-AudioCodes-SBC-FQDN");
        logger.debug("phone>>> callConfirmed", call, message, cause);
        if (sbcFqdnHeader) {
          this.FQDNServer = sbcFqdnHeader.startsWith("wss://")
            ? sbcFqdnHeader
            : "wss://" + sbcFqdnHeader;
        }
        logger.debug("FQDNServer: " + this.FQDNServer);

        this.#saveRestoreData();
      },
      callShowStreams: (call, local, remote) => {
        logger.debug("phone.callShowStreams, call: %o local: %o remote: %o", call, local, remote);
        this.localStream = local;
        this.currentRemoteStream = remote;
        this.hasActiveCall = true;
        if (this.audioRemoteElementRef) {
          this.audioRemoteElementRef.srcObject = remote;
          this.audioRemoteElementRef.volume = 1;

          if (this.callConnectedCallback) {
            this.callConnectedCallback();
          }
        } else {
          logger.warn("audioRemoteElementRef is null—cannot attach remote stream");
        }
      },
      incomingCall: (call, invite) => {
        logger.log("phone.incomingCall, call: %o invite: %o", call, invite);
      },
      callHoldStateChanged: (call, isHold, isRemote) => {
        logger.log(
          "phone.callHoldStateChanged call: %o isHold: %o isRemote: %o",
          call,
          isHold,
          isRemote,
        );
      },
    };
  }

  #retrievePrimaryIceServer(serverList, connectedServer) {
    const newServerList = [];
    connectedServer = connectedServer.toLowerCase();

    serverList.forEach((server) => {
      const serverArray = server.split(":");
      const url = `${serverArray[0].toLowerCase()}:${serverArray[1].toLowerCase()}`;
      const weight = serverArray.length > 2 ? parseInt(serverArray[2]) : 5;

      if (url === connectedServer) {
        newServerList.push([url, 1000]);
      } else {
        newServerList.push([url, weight]);
      }
    });

    return newServerList;
  }

  #saveRestoreData() {
    logger.info("Saving call restore data");
    const restoreData = {
      callTo: this.currentActiveCall.data["_user"],
      video: false,
      replaces: this.currentActiveCall.getReplacesHeader(),
      time: Date.now(),
      agentLegId: this.agentLegId,
      connectedServer: this.FQDNServer ?? this.phone.getServerAddress(),
    };

    localStorage.setItem("sessionPhoneRestoreCall", JSON.stringify(restoreData));
  }

  #clearRestoreData() {
    logger.info("Removing call restore data");
    localStorage.removeItem("sessionPhoneRestoreCall");
  }

  #getRestoreData() {
    let rawJson = localStorage.getItem("sessionPhoneRestoreCall");

    if (!rawJson) return null;

    try {
      return JSON.parse(rawJson);
    } catch (err) {
      logger.warn("Error parsing softphone restore data: " + err);
      this.#clearRestoreData();
    }

    return null;
  }

  /**
   * Attempt to restore an active softphone call from localStorage
   */
  #restoreSoftphoneCall() {
    logger.info("Attempting to restore saved softphone call");

    if (!this.phoneConnectionConfig.restoreCall) {
      logger.info("Call restoration disabled");
      return;
    }

    const restoreData = this.#getRestoreData();
    if (!restoreData) {
      logger.info("No call restore data available");
      return;
    }

    logger.debug("Call restore data", restoreData);

    const delay = Math.ceil(Math.abs(restoreData.time - Date.now()) / 1000);
    if (delay > this.phoneConnectionConfig.restoreCallMaxDelay) {
      logger.warn(
        `Call restoration skipped: delay ${delay}s exceeds max ` +
          `${this.phoneConnectionConfig.restoreCallMaxDelay}s`,
      );
      this.#clearRestoreData();
      return;
    }
    if (!restoreData.callTo?.trim()) {
      logger.warn("Skipping softphone restore: missing saved target");
      this.#clearRestoreData();
      return;
    }

    if (!this.phone || typeof this.phone.call !== "function") {
      logger.warn("Cannot restore call: softphone not initialized yet");
      return;
    }

    // All checks passed: restore the call
    const params = [
      "X-InContact-AgentLegId: " + restoreData.agentLegId,
      // TODO: is this needed? (TL doesn't seem to need it)
      "Replaces: " + restoreData.replaces,
    ];

    this.#clearRestoreData();
    const videoOption = !!restoreData.video;
    this.currentActiveCall = this.phone.call(videoOption, restoreData.callTo, params);

    logger.info("Softphone call restored", { call: this.currentActiveCall });
  }
}
